/* ══════════════════════════════════════════════════════════════════════════
 * ELEAT IN THE ELEATER'S OWN LANGUAGE — 5 October 2026.
 *
 * Al: "if they say Cantonese then everything is in Cantonese for them… we also
 * have to have Mandarin on the Hong Kong side."  Three languages, chosen once:
 *   en · zh-HK (繁體中文, Hong Kong) · zh-CN (简体中文, Mandarin)
 *
 * TWO KINDS OF WORDS, TWO PLACES THEY COME FROM:
 *   ELEAT'S words (buttons, headings, messages) — the dictionary in
 *     i18n_strings.js, written by tools/generators/ui_strings.py out of
 *     `9. Language Review/ui_translations/`, in OpenRice's register. The
 *     translator looks every text up whole and leaves anything it does not
 *     know exactly as it is.
 *   A RESTAURANT'S words (its name, its dishes, its cuisine, its area) — the
 *     server already sends them in Chinese (name_zh, *_zh, a dish's `tr`);
 *     the helpers below pick the reader's language and fall back to the
 *     restaurant's own words, never to nothing.
 *
 * WHICH LANGUAGE: English unless they chose one — the account's when signed
 * in, this visit's when not (voice.js screenLanguage; Al, 6 Oct 2026).
 * ══════════════════════════════════════════════════════════════════════════ */
import { eleatI18n, DICT, T2S } from './i18n_strings.js';
import { screenLanguage } from './voice.js';
import { API_BASE } from './api.js';

const engine = eleatI18n(DICT);
const S = new Map([...String(T2S.from)].map((c, i) => [c, T2S.to[i]]));

/** start translating the page — once, before the first screen draws */
let started = null;
export function startI18n() {
  const lang = engine.start(screenLanguage());
  started = lang;
  /* a correction made in Super Admin (138) reaches the screen without a new
     build — asked once, quietly; nothing changes when it cannot be reached */
  if (lang !== 'en' && API_BASE) {
    fetch(`${API_BASE}/i18n/${encodeURIComponent(lang)}?surface=app&human=1`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.strings) engine.merge(d.strings); })
      .catch(() => {});
  }
  return lang;
}

/** 'en' | 'zh-HK' | 'zh-CN' | 'fr-CA' | 'gu-IN' */
export function uiLang() {
  /* the language the page was started in — the stored choice can change
     under it (a city that uses fewer languages, 160) until the page reloads */
  return started || engine.norm(screenLanguage());
}

/** true when the stored choice no longer matches the language on screen */
export function languageMoved() {
  return !!started && engine.norm(screenLanguage()) !== started;
}

/** Eleat's own words, for the rare string built outside the page */
export function tx(s) {
  return engine.t(s);
}

/** Traditional → Simplified, for a Mandarin reader */
export function simplified(s) {
  return [...String(s || '')].map((c) => S.get(c) || c).join('');
}

/** a name the server sends in English and in Chinese (Traditional) */
export function zh(en, zhHant) {
  const l = uiLang();
  /* only a Chinese reader gets the Chinese name — a French reader (173) gets
     the restaurant's own words, never Chinese */
  if (!l.startsWith('zh') || !zhHant) return en;
  return l === 'zh-CN' ? simplified(zhHant) : zhHant;
}

/** a taxonomy label shaped { en, zh } (cuisines, facilities, venue types) */
export function label(o) {
  if (!o) return '';
  if (typeof o === 'string') return o;
  return zh(o.en || o.name_en || o.key, o.zh || o.name_zh);
}

/** a dish in the reader's language — its `tr` (013), else its own words */
export function dish(item) {
  const name = item.name_override || item.name;
  const tr = item.tr || {};
  const l = uiLang();
  const pickOne = tr[l] || (l === 'zh-CN' && tr['zh-HK']
    ? { name: simplified(tr['zh-HK'].name), description: tr['zh-HK'].description && simplified(tr['zh-HK'].description) }
    : null);
  /* a line typed onto a deal by name is translated too now — Zaryna writes
     it (139, Al 5 Oct 2026: "Zaryna translates deal wording"); without a
     translation the restaurant's own words stay */
  if (!pickOne || !pickOne.name) {
    return { name, description: item.description || null };
  }
  /* the restaurant's own words beside a translation — only when they differ
     (Zaryna's English check returns a correct English name unchanged) */
  const same = String(pickOne.name).trim().toLowerCase() === String(name).trim().toLowerCase();
  return { name: pickOne.name, description: pickOne.description || item.description || null, original: same ? null : name };
}

/** one entry of a `tr` map in the reader's language — Simplified made from
    the Hong Kong text when Zaryna has not written Mandarin yet */
function pickTr(tr) {
  const l = uiLang();
  if (l === 'en' || !tr) return null;
  if (tr[l]) return tr[l];
  if (l === 'zh-CN' && tr['zh-HK']) {
    const h = tr['zh-HK'];
    if (typeof h === 'string') return simplified(h);
    const o = {}; Object.keys(h).forEach((k) => { o[k] = h[k] == null ? h[k] : simplified(h[k]); });
    return o;
  }
  return null;
}

/* a title that is just the offer's own name is ELEAT's word, not the
   restaurant's — the dictionary says it (歡樂時光 · 每日優惠 · 限時特價 · 賽事日),
   never Zaryna's own wording of it ("即晚快閃優惠"), so it reads the same
   on every screen */
const OFFER_NAMES = new Set(['happy hour', 'daily deal', 'daily deals', 'last-minute special',
  'last-minute specials', 'last minute special', 'game day']);
const isOfferName = (t) => OFFER_NAMES.has(String(t || '').trim().toLowerCase());

/** a deal's own title and description in the reader's language (139) */
export function dealText(d) {
  const t = pickTr(d && d.tr) || {};
  return { title: isOfferName(d.title) ? d.title : (t.title || d.title),
    description: t.description || d.description || null };
}

/** a restaurant's tagline and About text in the reader's language (141) */
export function aboutText(v) {
  const t = pickTr(v && v.tr) || {};
  return { tagline: t.tagline || (v && v.tagline) || null, description: t.description || (v && v.description) || null };
}

/** the restaurant's own accessibility + amenity items (169, "Add your own"),
 *  in the reader's language when Zaryna has written them, else as typed */
export function ownFacilities(v) {
  const t = pickTr(v && v.tr) || {};
  const pick = (k) => (Array.isArray(t[k]) && t[k].length === ((v && v[k]) || []).length ? t[k] : (v && v[k]) || []);
  return [...pick('own_accessibility'), ...pick('own_amenities')].filter(Boolean);
}

/** a Feed post's caption in the reader's language (141) */
export function captionOf(f) {
  return pickTr(f && f.tr) || (f && f.caption) || null;
}

/** an offer chip on a card — its title in the reader's language (139) */
export function offerTitle(o) {
  return isOfferName(o.title) ? o.title : (pickTr(o && o.tr) || o.title);
}

/** a Home hot key's words in the reader's language (159), else its English */
export function hotKeyLabel(hk) {
  return pickTr(hk && hk.tr) || (hk && hk.label) || '';
}

/** the phrases on screen with no translation yet — for checking, never shown */
export function untranslated() {
  return engine.missing();
}
if (typeof window !== 'undefined') window.__eleatUntranslated = untranslated;
